import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { STANDARD_DATE_FORMATS } from '../date-time-helpers.constants';
import type { DateInput, FormatOptions } from '../date-time-helpers.type';
import { safeParseDateInput } from '../parsing/parsing';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);

/**
 * This is the core date formatting function
 * It can handle:
 * - Custom format string (uses Dayjs format tokens)
 * - Locale for localization (e.g., 'en', 'es', 'fr')
 * - Target timezone for conversion (e.g., 'America/New_York', 'UTC')
 * - Whether to treat input as UTC
 * - Fallback value when date parsing fails
 * - Whether to show relative time instead of absolute (e.g., '2 hours ago')
 * - Reference date for relative time calculations (defaults to now)
 *
 * @param {DateInput} dateInput - Date to format
 * @param {FormatOptions} [options] - Formatting options
 * @returns {string} Formatted date string
 */
const formatDate = (dateInput: DateInput, options?: FormatOptions): string => {
  const date = safeParseDateInput(dateInput);

  if (!date) {
    return options?.fallback || '';
  }

  const {
    format = STANDARD_DATE_FORMATS.FULL_DATE, // Default to standardized full date format
    locale = 'en',
    timezone,
    utc = false,
    fallback = '',
    relative = false,
    relativeTo,
  } = options || {};

  // Handle relative time formatting
  if (relative) {
    const reference = relativeTo ? safeParseDateInput(relativeTo) : dayjs();

    if (!date || !reference) {
      return fallback;
    }

    return date.from(reference);
  }

  try {
    let formattedDate = date;

    // Apply UTC if specified
    if (utc) {
      formattedDate = formattedDate.utc();
    }

    // Apply timezone if specified
    if (timezone) {
      formattedDate = formattedDate.tz(timezone);
    }

    // Apply locale if specified
    if (locale !== 'en') {
      formattedDate = formattedDate.locale(locale);
    }

    // Format the date
    return formattedDate.format(format);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return options?.fallback || '';
  }
};

/**
 * Formats a date to ISO string format
 *
 * @param {DateInput} dateInput - Date to format
 * @returns {string | null} ISO formatted string or null
 */
const formatToISOString = (dateInput: DateInput): string | null => {
  const date = safeParseDateInput(dateInput);

  if (!date) {
    return null;
  }

  return formatDate(date, { format: 'YYYY-MM-DDTHH:mm:ss.SSS[Z]' });
};

/**
 * ALIAS FUNCTIONS - Convenient shortcuts for common date formatting scenarios
 */

/**
 * Formats a date using the standardized month-year format (Sep, 2024)
 *
 * @param {DateInput} dateInput - Date to format
 * @param {Omit<FormatOptions, 'format'>} [options] - Formatting options
 * @returns {string} Formatted date string
 */
const formatMonthDate = (dateInput: DateInput, options?: Omit<FormatOptions, 'format'>): string => {
  return formatDate(dateInput, { ...options, format: STANDARD_DATE_FORMATS.MONTH_YEAR });
};

/**
 * Formats a date using the standardized quarter-year format (Q3 2024)
 *
 * @param {DateInput} dateInput - Date to format
 * @param {Omit<FormatOptions, 'format'>} [options] - Formatting options
 * @returns {string} Formatted date string
 */
const formatQuarterDate = (dateInput: DateInput, options?: Omit<FormatOptions, 'format'>): string => {
  return formatDate(dateInput, { ...options, format: STANDARD_DATE_FORMATS.QUARTER_YEAR });
};

/**
 * Formats a date using the standardized year-only format (2024)
 *
 * @param {DateInput} dateInput - Date to format
 * @param {Omit<FormatOptions, 'format'>} [options] - Formatting options
 * @returns {string} Formatted date string
 */
const formatYearDate = (dateInput: DateInput, options?: Omit<FormatOptions, 'format'>): string => {
  return formatDate(dateInput, { ...options, format: STANDARD_DATE_FORMATS.YEAR_ONLY });
};

/**
 * Formats a date range
 *
 * @param {DateInput} startDate - Start date
 * @param {DateInput} endDate - End date
 * @param {FormatOptions} [options] - Formatting options
 * @returns {string} Formatted date range string
 */
const formatDateRange = (startDate: DateInput, endDate: DateInput, options?: FormatOptions): string => {
  const start = safeParseDateInput(startDate);
  const end = safeParseDateInput(endDate);

  if (!start || !end) {
    return '';
  }

  return `${formatDate(start, options)} - ${formatDate(end, options)}`;
};

export { formatDate, formatDateRange, formatMonthDate, formatQuarterDate, formatToISOString, formatYearDate };
